import type { PipelineStep } from "@/lib/api";
import { getPipelineStatusCategory } from "@/lib/pipeline-status";
import { cleanText, asRecord } from "@/lib/utils";

function getSummary(data: Record<string, unknown>): string | null {
  const output = asRecord(data.output);
  const finalResult = asRecord(data.final_result);
  return (
    cleanText(data.summary) ??
    cleanText(output?.summary) ??
    cleanText(finalResult?.summary) ??
    null
  );
}

function getUserQuery(data: Record<string, unknown>): string | null {
  return cleanText(data.user_query) ?? cleanText(data.query) ?? null;
}

const PLAN_ITEM_LABEL_KEYS = [
  "title",
  "name",
  "label",
  "description",
  "task",
  "step",
  "action",
  "content",
  "text",
  "summary",
  "overview",
  "message",
  "instruction",
  "objective",
] as const;

/** Human-readable label for execution-plan steps, prerequisites, etc. */
export function formatPlanItemLabel(item: unknown): string {
  if (item == null) return "";
  if (typeof item === "string") return item.replace(/\r\n/g, "\n").trim();
  if (typeof item === "number" || typeof item === "boolean") {
    return String(item);
  }

  const obj = asRecord(item);
  if (!obj) return "";

  for (const key of PLAN_ITEM_LABEL_KEYS) {
    const val = obj[key];
    if (typeof val === "string" && val.trim()) return val.trim();
  }

  if (obj.step != null) {
    const nested = formatPlanItemLabel(obj.step);
    if (nested) return nested;
  }

  for (const val of Object.values(obj)) {
    if (typeof val === "string" && val.trim()) return val.trim();
  }

  return "";
}

function stepFromPartial(
  item: unknown,
  index: number,
  defaults?: Partial<PipelineStep>
): PipelineStep | null {
  if (item == null) return null;

  if (typeof item === "string") {
    const text = item.trim();
    if (!text) return null;
    return {
      id: defaults?.id ?? `step-${index}`,
      step_id: defaults?.step_id ?? `step-${index}`,
      logical_step_id: defaults?.logical_step_id ?? `step-${index}`,
      title: text,
      status: defaults?.status ?? "pending",
      agent_type: defaults?.agent_type ?? "Pipeline",
      started_at: defaults?.started_at ?? "",
      completed_at: defaults?.completed_at ?? "",
      retry_count: defaults?.retry_count ?? 0,
    };
  }

  const s = asRecord(item);
  if (!s) return null;

  const id = String(s.id ?? s.step_id ?? s.logical_step_id ?? `step-${index}`);
  const title = String(
    s.title ?? s.name ?? s.label ?? s.description ?? `Step ${index + 1}`
  );

  return {
    id,
    step_id: String(s.step_id ?? s.id ?? id),
    logical_step_id: String(s.logical_step_id ?? s.step_id ?? id),
    title,
    status: String(s.status ?? defaults?.status ?? "pending"),
    agent_type: String(s.agent_type ?? s.agent ?? defaults?.agent_type ?? "Agent"),
    input: s.input,
    result: s.result ?? s.output,
    started_at: String(s.started_at ?? ""),
    completed_at: String(s.completed_at ?? ""),
    total_tokens: typeof s.total_tokens === "number" ? s.total_tokens : undefined,
    total_cost_usd:
      typeof s.total_cost_usd === "number" ? s.total_cost_usd : undefined,
    retry_count: typeof s.retry_count === "number" ? s.retry_count : 0,
  };
}

/** Normalize varied API response shapes into a step list. */
export function parsePipelineStepsResponse(data: unknown): PipelineStep[] {
  if (!data) return [];

  if (Array.isArray(data)) {
    return data
      .map((item, i) => stepFromPartial(item, i))
      .filter((s): s is PipelineStep => s != null);
  }

  const root = asRecord(data);
  if (!root) return [];

  const candidates = [
    root.items,
    root.steps,
    root.results,
    asRecord(root.data)?.items,
    asRecord(root.data)?.steps,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      return candidate
        .map((item, i) => stepFromPartial(item, i))
        .filter((s): s is PipelineStep => s != null);
    }
  }

  return [];
}

function collectPlanStepStrings(plan: unknown): string[] {
  const strings: string[] = [];

  if (typeof plan === "string") {
    const lines = plan.split("\n");
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith("#")) continue;
      const numbered = trimmed.match(/^\d+[\.\)]\s+(.+)$/);
      if (numbered) {
        strings.push(numbered[1]);
        continue;
      }
      if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
        strings.push(trimmed.slice(2).trim());
        continue;
      }
      if (trimmed.toLowerCase().startsWith("task:")) {
        strings.push(trimmed);
      }
    }
    return strings;
  }

  const structured = asRecord(plan);
  if (!structured) return strings;

  const sections = asRecord(structured.sections) ?? structured;

  const prereqs = Array.isArray(sections.prerequisites)
    ? sections.prerequisites
    : [];
  for (const p of prereqs) {
    const text = formatPlanItemLabel(p);
    if (text) strings.push(`Prerequisite: ${text}`);
  }

  const steps = Array.isArray(sections.steps) ? sections.steps : [];
  for (const s of steps) {
    const text = formatPlanItemLabel(s);
    if (text) strings.push(text);
  }

  const overview =
    typeof sections.overview === "string" ? sections.overview.trim() : "";
  if (overview && strings.length === 0) strings.push(overview);

  return strings.filter(Boolean);
}

/** Steps derived from execution_plan on the run output payload. */
export function extractPlannedStepsFromRunOutput(
  data: Record<string, unknown>
): PipelineStep[] {
  const plans = [
    data.execution_plan,
    data.execution_plan_sections,
    asRecord(data.output)?.execution_plan,
  ];

  const strings: string[] = [];
  for (const plan of plans) {
    strings.push(...collectPlanStepStrings(plan));
  }

  const unique = [...new Set(strings)];
  const runStatus = data.status != null ? String(data.status) : "pending";

  return unique.map((title, i) =>
    stepFromPartial(title, i, {
      id: `plan-${i}`,
      step_id: `plan-${i}`,
      logical_step_id: `plan-${i}`,
      status: runStatus,
      agent_type: "Planned",
    })
  ).filter((s): s is PipelineStep => s != null);
}

/** Steps embedded directly on run output (not from /steps API). */
export function extractEmbeddedStepsFromRunOutput(
  data: Record<string, unknown>
): PipelineStep[] {
  const sources = [
    data.steps,
    data.pipeline_steps,
    data.execution_steps,
    asRecord(data.output)?.steps,
    asRecord(data.output)?.pipeline_steps,
    asRecord(data.trace)?.steps,
  ];

  for (const source of sources) {
    const parsed = parsePipelineStepsResponse(source);
    if (parsed.length > 0) return parsed;
  }

  return [];
}

/** Minimal lifecycle trace when API/plan lists are empty. */
export function buildLifecycleTraceFromRun(
  data: Record<string, unknown>
): PipelineStep[] {
  const status = data.status != null ? String(data.status) : "unknown";
  const category = getPipelineStatusCategory(status);
  const query = getUserQuery(data);
  const summary = getSummary(data);

  const steps: PipelineStep[] = [
    stepFromPartial("Job received", 0, {
      id: "lifecycle-submit",
      step_id: "lifecycle-submit",
      status: "completed",
      agent_type: "Orchestrator",
    })!,
  ];

  if (query) {
    steps.push(
      stepFromPartial(query, 1, {
        id: "lifecycle-task",
        step_id: "lifecycle-task",
        status:
          category === "running"
            ? "in_progress"
            : category === "success"
              ? "completed"
              : category === "failed"
                ? "failed"
                : status,
        agent_type: "Analysis",
      })!
    );
  }

  if (category === "running" && !query) {
    steps.push(
      stepFromPartial("Pipeline executing", 2, {
        id: "lifecycle-run",
        step_id: "lifecycle-run",
        status: "in_progress",
        agent_type: "Pipeline",
      })!
    );
  }

  if (summary && category === "success") {
    steps.push(
      stepFromPartial(summary, steps.length, {
        id: "lifecycle-result",
        step_id: "lifecycle-result",
        status: "completed",
        agent_type: "Result",
      })!
    );
  }

  return steps.filter(Boolean);
}

export type TraceStepSource = "api" | "embedded" | "plan" | "lifecycle";

export function resolveTraceSteps(
  apiSteps: PipelineStep[],
  runData?: Record<string, unknown> | null
): { steps: PipelineStep[]; source: TraceStepSource } {
  if (apiSteps.length > 0) return { steps: apiSteps, source: "api" };

  if (runData) {
    const embedded = extractEmbeddedStepsFromRunOutput(runData);
    if (embedded.length > 0) return { steps: embedded, source: "embedded" };

    const planned = extractPlannedStepsFromRunOutput(runData);
    if (planned.length > 0) return { steps: planned, source: "plan" };

    const lifecycle = buildLifecycleTraceFromRun(runData);
    if (lifecycle.length > 0) return { steps: lifecycle, source: "lifecycle" };
  }

  return { steps: [], source: "api" };
}
